import React, { useMemo } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { Widget } from 'features/types'
import IndexBubbleChart from 'components/visualization/IndexBubble'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { SocialWebDemographicWidget } from 'schemas/SocialWebDemographicWidgetSchema'
import { useDisplayTheme } from 'hooks/useUiState'
import InsightViewOption, { InsightViewOptionStrings } from 'enums/InsightViewOption'
import Gender, { GenderStrings } from 'enums/Gender'
import messageUtil from 'utils/messageUtil'
import { getTwEmojiImgUrl } from 'utils/emojiUtil'
import { getLabelByViewOption, getIndexValue, getDisplayIndex } from '../utils'
import useDemographicData from '../useDemographicData'

const customClassNameForMale = 'custom-male'
const customClassNameForFemale = 'custom-female'

const IndexBubbleContainer = styled(IndexBubbleChart)<{ mode: string }>`
  .${customClassNameForMale} {
    background-color: ${({ mode }) => sys[mode].semantic.male.default};
  }
  .${customClassNameForMale}:hover {
    background-color: ${({ mode }) => sys[mode].semantic.male.hover};
  }
  .${customClassNameForMale}:active {
    background-color: ${({ mode }) => sys[mode].semantic.male.pressed};
  }

  .${customClassNameForFemale} {
    background-color: ${({ mode }) => sys[mode].semantic.female.default};
  }
  .${customClassNameForFemale}:hover {
    background-color: ${({ mode }) => sys[mode].semantic.female.hover};
  }
  .${customClassNameForFemale}:active {
    background-color: ${({ mode }) => sys[mode].semantic.female.pressed};
  }
`

const getBubbleCustomClassName = (name: string, viewOption: InsightViewOptionStrings) => {
  switch (viewOption) {
    case InsightViewOption.MALE_FEMALE:
      return {
        [Gender.FEMALE]: customClassNameForFemale,
        [Gender.MALE]: customClassNameForMale
      }[name as GenderStrings]
    default:
      return undefined
  }
}

const DemographicIndexBubble: Widget = ({ widgetId }) => {
  const theme = useDisplayTheme()
  const { viewOption, countType } = useDashboardWidgetSetting<SocialWebDemographicWidget>(widgetId)
  const { insights } = useDemographicData(widgetId)
  const { t } = useTranslation()

  const data = useMemo(
    () =>
      insights.map((insight) => ({
        x: getIndexValue(insight.index!),
        z: insight.value || 0,
        name:
          viewOption === InsightViewOption.BIOTEXT
            ? insight.name!
            : (getLabelByViewOption(insight.name!, viewOption!) as string),
        customClassName: getBubbleCustomClassName(insight.name!, viewOption!),
        imgUrl:
          viewOption === InsightViewOption.BIOTEXT ? getTwEmojiImgUrl(insight.name!) : undefined
      })),
    [viewOption, insights]
  )

  return (
    <IndexBubbleContainer
      mode={theme}
      data={data}
      xTitle={t('widgetType.benchmark')}
      zTitle={messageUtil.viewOption(countType!)}
      primaryTickLeftAnnotation={t('underIndexed')}
      primaryTickRightAnnotation={t('overIndexed')}
      primaryTickExtensionBreakpoint={140}
      xValueFormatter={getDisplayIndex}
    />
  )
}

export default DemographicIndexBubble
